import { useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { queueForReauth } from "@/components/ReauthDialog";

const RENEW_MS = 30 * 60 * 1000;

const REVOKED = /disabled by admin|Session ended/i;

// Pings the server every 60 s while the app is open (Live presence) and
// signs the user out at once when an admin disables / force-logs them out.
export const useSessionGuard = (isAuthenticated, setIsAuthenticated) => {
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const kick = (msg) => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast.error(msg, { duration: 10000 });
      setIsAuthenticated(false);
    };
    const id = axios.interceptors.response.use(undefined, (err) => {
      const d = err.response?.data?.detail;
      if (err.response?.status === 401 && typeof d === "string" && REVOKED.test(d)) kick(d);
      return queueForReauth(err) || Promise.reject(err);
    });
    // Sliding session: while the app is in use, swap in a fresh 7-day token every 30 min.
    let lastRenew = 0;
    const renew = () => {
      if (document.visibilityState === "hidden" || Date.now() - lastRenew < RENEW_MS) return;
      lastRenew = Date.now();
      axios.post(`${API}/auth/renew`, {}, { headers: getAuthHeader() })
        .then((r) => { if (r.data?.token) localStorage.setItem("token", r.data.token); }).catch(() => {});
    };
    const beat = () => {
      if (document.visibilityState === "hidden") return;
      axios.post(`${API}/auth/heartbeat`, {}, { headers: getAuthHeader() }).catch(() => {});
    };
    const tick = () => { beat(); renew(); };
    tick();
    const t = setInterval(tick, 60000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", tick);
      axios.interceptors.response.eject(id);
    };
  }, [isAuthenticated, setIsAuthenticated]);
};
